import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { accountApi, statsApi, formatCurrency, getTextColor } from '../services/api';

const AccountSection = styled.div`
  margin-bottom: 30px;
`;

const AccountTypeHeader = styled.h3`
  margin: 0 0 15px 0;
  padding: 0;
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
`;

const AccountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(100%, 1fr));
    gap: 15px;
  }
`;

const CategoryBreak = styled.div`
  grid-column: 1 / -1;
  height: 1px;
`;

const AccountCard = styled.div`
  background-color: ${props => props.color || '#f8f9fa'};
  border-radius: 16px;
  height: 100px;
  box-shadow: ${props => props.$dragOver
    ? '0 0 0 3px #2a78d6, 0 4px 12px rgba(0, 0, 0, 0.15)'
    : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  transition: transform 0.2s ease-in-out, box-shadow 0.15s ease-in-out;
  cursor: grab;
  display: flex;
  align-items: center;
  padding: 16px 16px 16px 16px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .account-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    font-weight: 800;
    font-size: 16px;
    color: #000000;
  }
  
  .account-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  .account-name {
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 4px;
    color: ${props => props.textColor || '#333'};
  }
  
  .account-balance {
    font-size: 22px;
    font-weight: 800;
    color: ${props => props.textColor || '#333'};
  }
`;

const AddAccountButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  font-size: 2rem;
  cursor: pointer;
  position: fixed;
  bottom: 30px;
  right: 30px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: #218838;
    transform: scale(1.1);
  }
`;

const TotalAssets = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  
  .total-amount {
    font-size: 1.8rem;
    font-weight: 700;
    color: #007bff;
    text-align: center;
  }
`;

function AccountList() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalCash, setTotalCash] = useState(0);
  const [dragOverAccountId, setDragOverAccountId] = useState(null);
  const accountDragRef = useRef(null);

  useEffect(() => {
    fetchAccounts();
    fetchTotalAssets();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await accountApi.getAllAccounts();

      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid accounts data received:', response.data);
        return;
      }

      // 예전에는 여기서 투자 계좌마다 평가기록을 따로 한 번씩 더 받아 왔는데,
      // 이 화면은 그걸 쓰지 않았다(투자 계좌는 evaluated_amount + cash_balance 만 본다).
      // 그 요청마다 서버가 DB 를 통째로 읽었으니 홈 화면만 열어도 십여 번을 읽은 셈이다.

      // Sort accounts to match the desktop app behavior
      const sortedAccounts = [...response.data].sort((a, b) => {
        // First by status (active first)
        if (a.status === "dead" && b.status !== "dead") return 1;
        if (a.status !== "dead" && b.status === "dead") return -1;

        // Then by type order
        const typeOrder = { "현금": 0, "투자": 1, "소비": 2 };
        const aTypeOrder = typeOrder[a.type] !== undefined ? typeOrder[a.type] : 3;
        const bTypeOrder = typeOrder[b.type] !== undefined ? typeOrder[b.type] : 3;
        if (aTypeOrder !== bTypeOrder) return aTypeOrder - bTypeOrder;

        // Finally by name (민규 first)
        const aHasMinGyu = a.name.includes("민규") ? 0 : 1;
        const bHasMinGyu = b.name.includes("민규") ? 0 : 1;
        return aHasMinGyu - bHasMinGyu;
      });
      setAccounts(sortedAccounts);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const fetchTotalAssets = async () => {
    try {
      const [assetsResponse, cashResponse] = await Promise.all([
        statsApi.getTotalAssets(),
        statsApi.getTotalCash()
      ]);
      setTotalAssets(assetsResponse.data.total_assets);
      setTotalCash(cashResponse.data.total_cash);
    } catch (error) {
      console.error('Error fetching total assets:', error);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₩0';
    return `₩${amount.toLocaleString()}`;
  };

  const calculateBalance = (account) => {
    if (account.type === '투자') {
      // 자산 = 평가금액(보유 주식) + 예수금 - 백엔드가 항상 최신값으로 계산해서 내려주는 실제 필드를 그대로 사용
      // (asset_value는 API 응답에 직렬화되지 않는 프로퍼티라 evaluated_amount/cash_balance로 직접 계산)
      return (account.evaluated_amount || 0) + (account.cash_balance || 0);
    }

    // 현금·소비 계좌는 서버가 기초잔액 + 입금 - 출금을 계산해 보낸다.
    // 예전에는 계좌의 거래 전부를 받아 와 여기서 더했는데, 그러느라 홈 화면
    // 응답에 모든 거래가 실려 다녔다.
    return account.balance ?? account.opening_balance;
  };

  // Separate active and dead accounts
  const activeAccounts = accounts.filter(account => account.status !== "dead");
  const deadAccounts = accounts.filter(account => account.status === "dead");
  
  // Categorize investment accounts
  const categorizeInvestmentAccount = (account) => {
    const name = account.name;
    if (name.includes("개인연금")) return "개인연금";
    if (name.includes("퇴직연금")) return "퇴직연금";
    if (name.includes("코인")) return "코인";
    if (name.includes("부동산")) return "부동산";
    if (name.includes("주식") || name.includes("증권") || name.includes("나무") ||
        name.includes("한국투자") || name.includes("IBK") || name.includes("OK저축은행")) {
      return "주식";
    }
    return "기타";
  };

  // 이름에 포함된 사람으로 정렬 순서 결정 (민규 먼저, 하영 다음, 표기 없으면 마지막)
  const personOrder = (account) => {
    if (account.name.includes("민규")) return 0;
    if (account.name.includes("하영")) return 1;
    return 2;
  };

  // Group active accounts by type
  const groupedActiveAccounts = activeAccounts.reduce((groups, account) => {
    const type = account.type;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(account);
    return groups;
  }, {});

  // Sort investment accounts by category, then by person within each category
  if (groupedActiveAccounts["투자"]) {
    groupedActiveAccounts["투자"].sort((a, b) => {
      const categoryA = categorizeInvestmentAccount(a);
      const categoryB = categorizeInvestmentAccount(b);

      // Define category order
      const categoryOrder = { "주식": 0, "개인연금": 1, "퇴직연금": 2, "코인": 3, "부동산": 4, "기타": 5 };
      const orderA = categoryOrder[categoryA] !== undefined ? categoryOrder[categoryA] : 6;
      const orderB = categoryOrder[categoryB] !== undefined ? categoryOrder[categoryB] : 6;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // Same category: 민규 계좌 먼저, 하영 계좌 다음
      const personA = personOrder(a);
      const personB = personOrder(b);
      if (personA !== personB) {
        return personA - personB;
      }

      // Same category and person: preserve the manually-dragged order (sort_order from
      // the backend, already reflected in the array's incoming order) via stable sort
      return 0;
    });
  }

  // 드래그로 순서를 바꿀 수 있는 범위: 투자 계좌는 같은 카테고리+사람 안에서만,
  // 그 외 계좌는 같은 유형(type)+사람 안에서만 (기존 "민규 먼저" 구획을 그대로 존중)
  const groupKeyForAccount = (account, type) => {
    if (type === "투자") {
      return `${type}:${categorizeInvestmentAccount(account)}:${personOrder(account)}`;
    }
    return `${type}:${personOrder(account)}`;
  };

  const accountsByGroupKey = {};
  Object.entries(groupedActiveAccounts).forEach(([type, accountsOfType]) => {
    accountsOfType.forEach((account) => {
      const key = groupKeyForAccount(account, type);
      (accountsByGroupKey[key] = accountsByGroupKey[key] || []).push(account);
    });
  });

  const handleAccountDragStart = (event, groupKey, accountId) => {
    accountDragRef.current = { groupKey, accountId };
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', accountId);
  };

  const handleAccountDragOver = (event, groupKey, accountId) => {
    const source = accountDragRef.current;
    if (!source || source.groupKey !== groupKey || source.accountId === accountId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverAccountId(accountId);
  };

  const handleAccountDrop = async (event, groupKey, targetAccountId, groupAccounts) => {
    event.preventDefault();
    const source = accountDragRef.current;
    setDragOverAccountId(null);
    if (!source || source.groupKey !== groupKey || source.accountId === targetAccountId) return;

    const fromIndex = groupAccounts.findIndex((a) => a.id === source.accountId);
    const toIndex = groupAccounts.findIndex((a) => a.id === targetAccountId);
    if (fromIndex < 0 || toIndex < 0) return;

    const reordered = [...groupAccounts];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const groupIds = new Set(groupAccounts.map((a) => a.id));
    const previousAccounts = accounts;
    setAccounts((prev) => {
      const next = [...prev];
      let idx = 0;
      for (let i = 0; i < next.length; i++) {
        if (groupIds.has(next[i].id)) {
          next[i] = reordered[idx++];
        }
      }
      return next;
    });

    try {
      await accountApi.reorderAccounts(reordered.map((a) => a.id));
    } catch (error) {
      console.error('Error reordering accounts:', error);
      setAccounts(previousAccounts);
      alert('계좌 순서 변경에 실패했습니다.');
    }
  };

  const handleAccountDragEnd = () => {
    setDragOverAccountId(null);
    setTimeout(() => {
      accountDragRef.current = null;
    }, 0);
  };

  return (
    <div>
      <TotalAssets>
        <div className="total-amount">
          총 자산: {formatCurrency(totalAssets)} / 현금: {formatCurrency(totalCash)}
        </div>
      </TotalAssets>

      {Object.entries(groupedActiveAccounts).map(([type, accountsOfType]) => (
        <AccountSection key={type}>
          <AccountTypeHeader>{type} 계좌</AccountTypeHeader>
          <AccountGrid>
            {accountsOfType.map((account, index) => {
              const bgColor = account.color;
              const textColor = getTextColor(bgColor);
              console.log(`Account ${account.name}: bgColor=${bgColor}, textColor=${textColor}`); // Debug log
              const groupKey = groupKeyForAccount(account, type);
              const groupAccounts = accountsByGroupKey[groupKey] || [account];
              // 투자 계좌는 카테고리가 바뀌는 지점마다 줄바꿈을 넣어 카테고리 구분이 눈에 띄게 함
              const showCategoryBreak = type === "투자" && index > 0 &&
                categorizeInvestmentAccount(account) !== categorizeInvestmentAccount(accountsOfType[index - 1]);
              return (
                <React.Fragment key={account.id}>
                  {showCategoryBreak && <CategoryBreak />}
                  <AccountCard
                    color={bgColor}
                    textColor={textColor}
                    $dragOver={dragOverAccountId === account.id}
                    draggable
                    onDragStart={(event) => handleAccountDragStart(event, groupKey, account.id)}
                    onDragOver={(event) => handleAccountDragOver(event, groupKey, account.id)}
                    onDrop={(event) => handleAccountDrop(event, groupKey, account.id, groupAccounts)}
                    onDragEnd={handleAccountDragEnd}
                    onClick={() => {
                      if (accountDragRef.current) return;
                      navigate(`/account/${account.id}`);
                    }}
                  >
                    <div className="account-icon">
                      {account.name.charAt(0)}
                    </div>
                    <div className="account-info">
                      <div className="account-name">{account.name}</div>
                      <div className="account-balance">
                        {formatCurrency(calculateBalance(account))}
                      </div>
                    </div>
                  </AccountCard>
                </React.Fragment>
              );
            })}
          </AccountGrid>
        </AccountSection>
      ))}

      {deadAccounts.length > 0 && (
        <AccountSection>
          <AccountTypeHeader>비활성화된 계좌</AccountTypeHeader>
          <AccountGrid>
            {deadAccounts.map(account => {
              const bgColor = "#808080";
              const textColor = getTextColor(bgColor);
              return (
                <AccountCard
                  key={account.id}
                  color={bgColor}
                  textColor={textColor}
                  onClick={() => navigate(`/account/${account.id}`)}
                >
                  <div className="account-icon">
                    {account.name.charAt(0)}
                  </div>
                  <div className="account-info">
                    <div className="account-name">{account.name}</div>
                    <div className="account-balance">
                      {formatCurrency(calculateBalance(account))}
                    </div>
                  </div>
                </AccountCard>
              );
            })}
          </AccountGrid>
        </AccountSection>
      )}

      <AddAccountButton onClick={() => window.dispatchEvent(new CustomEvent('openAddAccountModal'))}>
        +
      </AddAccountButton>
    </div>
  );
}

export default AccountList;
